import { useEffect, useRef } from 'react';
import queryString from '@shared/core/queryString';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

let hasHydrated = false;

// In preview mode (admin website designer), we want animations and canvas
// rendering — never skip. Use the explicit ?preview=true query param rather
// than isInIframe() since iframes can appear in other legitimate contexts.
const isPreviewing = queryString.getValueString('preview') === 'true';

/**
 * Returns `true` on the first guest-facing SSR mount (before hydration
 * completes), `false` on all subsequent mounts and in preview mode.
 * The returned value is stable for the component instance's lifetime
 * (captured in a ref).
 *
 * When `true`:
 * - `AnimatedImageBox` skips the fade-in animation (photo visible immediately)
 * - `CroppedImage` uses CSS `background-image` instead of canvas
 *   (canvas requires JS + `useSizeObserver` which returns 0 on the server)
 *
 * When `false` (subsequent mounts, preview mode):
 * - Fade-in animation plays normally
 * - Canvas rendering for pixel-perfect crop editing
 *
 * NOTE: Consider removing the canvas path entirely and using CSS
 * `background-image` for all guest site rendering (not just SSR).
 * The CSS approach is visually sufficient and avoids the jarring
 * switch from background-image → canvas after hydration. Canvas
 * would only remain for the admin preview where pixel-perfect crop
 * editing matters. See OptimizedEventGuestSitePhoto/renditions.ts
 * for the centralized rendition strategy.
 */
export const useSkipInitialAnimation = (): boolean => {
  const [isFeatureEnabled] = useIsFeatureEnabled('websiteInitialHeaderAnimationDisabled');

  // Capture the decision in a ref so it's stable for the component's lifetime.
  // This prevents the feature flag's loading state (false → true) from
  // re-triggering the CSS animation after the initial render.
  const skipRef = useRef<boolean | null>(null);
  if (skipRef.current === null) {
    skipRef.current = Boolean(isFeatureEnabled) && !isPreviewing && !hasHydrated;
  }

  useEffect(() => {
    hasHydrated = true;
  }, []);
  return skipRef.current;
};
